import React from "react";
import { Youtube, Instagram, Music2, Play, ExternalLink } from "lucide-react";
import { useVideo, TRACK_VIDEOS } from "./VideoContext";

// Title → YouTube id, in the order the user provided
const VIDEOS = [
  { title: "Cupid", id: TRACK_VIDEOS["Cupid"], tag: "Opener" },
  { title: "Flowsy Fly", id: TRACK_VIDEOS["Flowsy Fly"], tag: "Single" },
  { title: "Anatomic Impulse", id: TRACK_VIDEOS["Anatomic Impulse"], tag: "Heavy" },
  { title: "Raft of Time", id: TRACK_VIDEOS["Raft of Time"], tag: "Atmospheric" },
  { title: "Love Sick Edifice", id: TRACK_VIDEOS["Love Sick Edifice"], tag: "Ballad" },
  { title: "Falling With Style", id: TRACK_VIDEOS["Falling With Style"], tag: "Single" },
];

const CHANNELS = [
  {
    id: "yt",
    icon: Youtube,
    platform: "YouTube",
    title: "@conceptsama8453",
    description: "Full videos, visualizers, and song drops — subscribe for the channel.",
    href: "https://youtube.com/@conceptsama8453",
    accent: "#FF0033",
  },
  {
    id: "ig",
    icon: Instagram,
    platform: "Instagram",
    title: "@concept.and.formality",
    description: "Studio sessions, behind-the-scenes, daily reels.",
    href: "https://www.instagram.com/concept.and.formality",
    accent: "#FF00E5",
  },
  {
    id: "tt",
    icon: Music2,
    platform: "TikTok",
    title: "@conceptandformality",
    description: "Short-form drops, riff clips, and tour updates.",
    href: "https://www.tiktok.com/@conceptandformality",
    accent: "#00F0FF",
  },
];

function VideoCard({ vid, index }) {
  const { open } = useVideo();
  const thumb = `https://img.youtube.com/vi/${vid.id}/maxresdefault.jpg`;
  return (
    <button
      data-testid={`video-card-${vid.id}`}
      onClick={() => open(vid.id)}
      className="glass-card group relative overflow-hidden aspect-video reveal text-left w-full"
      style={{ transitionDelay: `${index * 80}ms` }}
      aria-label={`Play ${vid.title} video`}
    >
      <img
        src={thumb}
        alt={vid.title}
        className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1s]"
        onError={(e) => { e.target.src = `https://img.youtube.com/vi/${vid.id}/hqdefault.jpg`; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-[#050508]/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#FF00E5]/0 via-transparent to-[#00F0FF]/0 group-hover:from-[#FF00E5]/25 group-hover:to-[#00F0FF]/15 transition-all duration-500" />

      {/* Play icon */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-black/60 backdrop-blur-md border border-[#FF00E5]/60 group-hover:border-[#FF00E5] group-hover:scale-110 transition-all duration-300 glow-magenta">
          <Play size={22} className="text-[#FF00E5] ml-1 fill-[#FF00E5]" />
        </span>
      </div>

      {/* Top tag */}
      <div className="absolute top-4 left-4">
        <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] px-2 py-1 bg-black/70 border border-[#00F0FF]/40 text-[#00F0FF]">
          {vid.tag}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
        <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
          Official Video
        </span>
        <h3 className="font-display font-bold text-lg md:text-xl text-cream-soft mt-0.5 leading-tight">
          {vid.title}
        </h3>
      </div>
    </button>
  );
}

export default function Media() {
  return (
    <section id="media" data-testid="media-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center mb-14 reveal">
          <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#FF00E5]">
            04 — Media
          </span>
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 text-cream-soft">
            WATCH <span className="text-gradient-neon">+ FOLLOW</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-white/60 text-sm md:text-base">
            Five tracks live on YouTube. Tap to play in-page.
          </p>
        </div>

        {/* Video grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {VIDEOS.map((v, i) => (
            <VideoCard key={v.id} vid={v} index={i} />
          ))}
        </div>

        {/* Channels row */}
        <div className="mt-14 reveal">
          <h3 className="font-mono-display text-[11px] uppercase tracking-[0.3em] text-white/55 mb-5">
            // Channels
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CHANNELS.map((c) => {
              const Tag = c.href === "#" ? "div" : "a";
              return (
                <Tag
                  key={c.id}
                  data-testid={`channel-${c.id}`}
                  {...(c.href !== "#" && { href: c.href, target: "_blank", rel: "noopener noreferrer" })}
                  className="glass-card group p-5 md:p-6 flex items-start gap-4 hover:border-[#00F0FF]/40 transition-all"
                >
                  <span
                    className="flex items-center justify-center w-11 h-11 border bg-black/60 group-hover:scale-110 transition-transform shrink-0"
                    style={{ borderColor: `${c.accent}50` }}
                  >
                    <c.icon size={16} style={{ color: c.accent }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55">
                      {c.platform} {c.href !== "#" && <ExternalLink size={10} />}
                    </div>
                    <div className="font-display font-bold text-base md:text-lg text-cream-soft mt-0.5 truncate">
                      {c.title}
                    </div>
                    <p className="mt-1 text-xs md:text-sm text-white/55 line-clamp-2">{c.description}</p>
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
